import { Button, Modal, Spin } from 'antd';
import { Viewport } from 'components/ViewportProvider';
import moment from 'moment';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Location, Store } from 'types/schema';
import formatFullAddress from 'utils/formatFullAddress';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import triggerGA4EcommerceEvent, { EventName } from 'utils/triggerGA4Event';
import money from '../../utils/formatMoney';
import Label from './Label';
import notAvailableProductImage from './not_available_product_image.svg';
import OrderTable from './OrderTable';
import StepCol from './StepCol';
import StepContainer from './StepContainer';
import StepItem from './StepItem';
import StepRow, { StepBox } from './StepRow';
import Value from './Value';

import InValidVoucherPopup from 'components/Checkout/Voucher/InValidVoucherPopup';
import { ErrorBoundary } from 'components/ErrorBoundary';
import ServiceFeeNote from 'components/ServiceFeeNote';
import VatTooltip from 'components/VatTooltip';
import { VOUCHER_STATUS } from 'containers/CheckoutPage/constants';
// import ServiceFeeNote from 'components/ServiceFeeNote';

const ButtonGoToCart = styled(Button)`
  padding: 8px auto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  width: 200px;
  height: 40px;
`;

const Step3StepCol = styled(StepCol)`
  gap: 20px;
  &.d-flex {
    display: flex;
  }
  &.justify-content-between {
    justify-content: space-between;
  }
  &.padding-bottom-5 {
    padding-bottom: 5px;
  }
  &.flex-column {
    flex-direction: column;
  }
  &.flex-basis-auto {
    flex-basis: auto;
  }

  ${({ width }) =>
    `
    width: ${width};
  `}
`;

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

const Image = styled.img``;

const Title = styled.div`
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  color: #242527;
  text-align: center;
`;

const Description = styled.div`
  margin-top: 4px;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: #585a60;
  text-align: center;
`;

const PaymentInformationContainer = styled.div`
  padding: 16px 12px;
  background-color: #fff;
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-top: 1px solid #dce0e9;
  ${media.md`
    padding: 20px 16px;
    align-items: flex-start;
    flex-direction: row;
    gap: 0px;
    justify-content: space-between;
  `}
`;

const PaymentInformationRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  ${media.md`
    width: 350px;
  `}
`;

const PaymentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Space = styled.div`
  ${({ height }) =>
    `
    height: ${height}px;
  `}
`;

const Divider = styled.div`
  margin: 8px 0px;
  width: 100%;
  height: 1px;
  background-color: #dce0e9;

  ${media.md`
    width: 350px;
  `}
`;

const PaymentInformationLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #343538;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;

  ${media.md`
    font-size: 16px;
    line-height: 22px;
  `}
`;

const DiscountValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #43a047;

  ${media.md`
    font-size: 16px;
    line-height: 22px;
  `}
`;

const TotalLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  color: #343538;
  ${media.sm`
    font-size: 14px;
  `}
`;

const TotalValue = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  color: #343538;
`;

const FooterAction = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  ${media.md`
    width: 350px;
  `}
`;

const ButtonWrapper = styled.div`
  width: 100%;
  a,
  button {
    flex: 1;
    width: 100%;
  }
  a {
    display: block;
  }
`;

interface CheckoutStep3Props {
  checkout: any;
  checkoutLoading: boolean;
  loading: boolean;
  cart: any;
  onGoPreviousStep: () => void;
  onCheckout: () => void;
  orderDisabled: boolean;
  store: Store;
  openModalSomthingChanged: boolean;
  onGoToStep1: (data: boolean) => void;
  discountedValue: number;
  voucherStatus: VOUCHER_STATUS | null;
}

export default ({
  checkout,
  cart,
  loading,
  checkoutLoading,
  onGoPreviousStep,
  onCheckout,
  orderDisabled,
  store,
  openModalSomthingChanged,
  onGoToStep1,
  discountedValue: discountValue,
  voucherStatus,
}: CheckoutStep3Props) => {
  const onCheckoutCompleted = () => {
    triggerGA4EcommerceEvent(EventName.ADD_PAYMENT_INFO, {
      ...cart?.cartSet[0],
    });
    onCheckout();
  };

  const notIncludeAlcoholProduct = () => {
    return cart?.cartSet?.every((item) => {
      const isIncludesAlcoholProduct = item?.items?.some((it) => it?.product?.category?.parent?.name === 'Alcohol');
      return !isIncludesAlcoholProduct;
    });
  };

  const totalVat = useMemo(() => {
    const serviceFeeVat = cart?.deliveryFee?.isFreeDelivery ? 0 : cart?.deliveryFee?.additionalVat;
    const totalVatOnProducts = cart?.cartSet?.[0]?.totalVatOnProducts || 0;
    return serviceFeeVat + totalVatOnProducts;
  }, [cart?.deliveryFee, cart?.cartSet]);

  const subTotal = useMemo(() => {
    if (cart?.cartSet?.[0]?.subTotalBeforeVat) {
      return cart?.cartSet?.[0]?.subTotalBeforeVat;
    }
    return 0;
  }, [cart]);

  const finalTotal = useMemo(() => {
    const serviceFee = cart?.deliveryFee?.isFreeDelivery ? 0 : cart?.deliveryFee?.fee;
    const discountVoucher = discountValue > 0 ? discountValue : 0;
    return subTotal + serviceFee - discountVoucher + totalVat;
  }, [subTotal, cart?.deliveryFee, discountValue, totalVat]);

  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isMobileSize = width < 768;
        return (
          <StepContainer>
            <Spin spinning={loading}>
              {!!cart?.cartSet?.length &&
                cart?.cartSet?.map((sup: any, index) => {
                  return (
                    <ErrorBoundary key={`step-3-${index}`}>
                      <StepItem>
                        {isMobileSize ? (
                          <StepBox className="confirm-mobile">
                            <StepRow className="justify-content-center">
                              <FormattedMessage {...utilsMessages.ordersConfirmMobileDisplay} />
                            </StepRow>
                          </StepBox>
                        ) : null}
                        <StepBox>
                          <StepRow className="justify-content-between">
                            {isMobileSize ? (
                              <Step3StepCol className="d-flex flex-column flex-basis-auto">
                                <div>
                                  <Label>
                                    <FormattedMessage {...utilsMessages.deliveryDate} />
                                  </Label>
                                  <Value>
                                    {moment(sup.deliveryDatetime.start).format('HH:mm')} -{' '}
                                    {moment(sup.deliveryDatetime.end).format('HH:mm')}
                                  </Value>
                                  <Value>{moment(sup.deliveryDatetime.start).format('DD/MM/YYYY')}</Value>
                                </div>
                                <div>
                                  <Label>
                                    <FormattedMessage {...utilsMessages.deliveryAddress} />
                                  </Label>
                                  <Value>
                                    {formatFullAddress({
                                      ...(store.newMergedLocation as Location),
                                      noteForAddress: store?.addressNote || '',
                                    })}
                                  </Value>
                                </div>
                              </Step3StepCol>
                            ) : (
                              <>
                                <Step3StepCol>
                                  <Label>
                                    <FormattedMessage {...utilsMessages.totalItem} />
                                  </Label>
                                  <Value>
                                    {sup.items.length}
                                    &nbsp;
                                    <FormattedMessage {...utilsMessages.items} />
                                  </Value>
                                </Step3StepCol>
                                <Step3StepCol>
                                  <Label>
                                    <FormattedMessage {...utilsMessages.deliveryTime} />
                                  </Label>
                                  <Value nowrap>
                                    {moment(sup.deliveryDatetime.start).format('HH:mm')} -{' '}
                                    {moment(sup.deliveryDatetime.end).format('HH:mm')} |{' '}
                                    {moment(sup.deliveryDatetime.start).format('DD/MM/YYYY')}
                                  </Value>
                                </Step3StepCol>
                                <Step3StepCol width="50%">
                                  <Label>
                                    <FormattedMessage {...utilsMessages.deliveryAddress} />
                                  </Label>
                                  <Value>
                                    {formatFullAddress({
                                      ...(store.newMergedLocation as Location),
                                      noteForAddress: store?.addressNote || '',
                                    })}
                                  </Value>
                                </Step3StepCol>
                              </>
                            )}
                          </StepRow>
                        </StepBox>
                        {(!!sup.deliveryNotes.length || !!cart?.cartSet?.[0]?.invoiceNote) && (
                          <StepBox>
                            <StepRow
                              gap={isMobileSize ? '20px' : '0px'}
                              flexDirection={isMobileSize ? 'column' : 'row'}
                            >
                              {!!sup.deliveryNotes.length && (
                                <StepCol span={1}>
                                  <Label>
                                    <FormattedMessage {...utilsMessages.noteForDelivery} />
                                  </Label>
                                  {sup.deliveryNotes.map((n: string, index: number) => (
                                    <Value key={index}>{n}</Value>
                                  ))}
                                </StepCol>
                              )}

                              {!!cart?.cartSet?.[0]?.invoiceNote && (
                                <StepCol span={1}>
                                  <Label>
                                    <FormattedMessage {...utilsMessages.invoiceNote} />
                                  </Label>
                                  <Value>{cart?.cartSet?.[0]?.invoiceNote}</Value>
                                </StepCol>
                              )}
                            </StepRow>
                          </StepBox>
                        )}
                        <OrderTable
                          name={`step-3-${index}`}
                          singleSupplierCart={sup}
                          productsQuantity={checkout.quantity}
                          disabled={true}
                        />
                        <PaymentInformationContainer>
                          <PaymentInformationRow>
                            <Label>
                              <FormattedMessage {...utilsMessages.paymentMethod} />
                            </Label>
                            <Value>
                              {notIncludeAlcoholProduct() ? (
                                <FormattedMessage {...utilsMessages[sup.paymentMethod]} />
                              ) : (
                                <FormattedMessage {...utilsMessages.BANK_TRANSFER} />
                              )}
                            </Value>
                          </PaymentInformationRow>
                          <PaymentDetails>
                            {isMobileSize && <Divider />}
                            <PaymentInformationRow>
                              <PaymentInformationLabel>
                                <FormattedMessage {...utilsMessages.subTotal} />
                              </PaymentInformationLabel>
                              <PaymentInformationLabel>{money(subTotal)}đ</PaymentInformationLabel>
                            </PaymentInformationRow>
                            <PaymentInformationRow>
                              <PaymentInformationLabel>
                                <FormattedMessage {...utilsMessages.serviceFee} />
                                {!cart?.deliveryFee?.isFreeDelivery && (
                                  <ServiceFeeNote
                                    deliveryFeeThreshold={cart?.deliveryFee?.deliveryFeeThreshold}
                                    moq={cart?.store?.moq}
                                    deliveryFee={cart?.deliveryFee?.fee}
                                    areaGroupName={cart?.deliveryFee?.areaGroupName}
                                  />
                                )}
                              </PaymentInformationLabel>
                              <PaymentInformationLabel>{money(cart?.deliveryFee?.fee)}đ</PaymentInformationLabel>
                            </PaymentInformationRow>

                            {cart?.deliveryFee?.isFreeDelivery && (
                              <PaymentInformationRow>
                                <PaymentInformationLabel>
                                  <FormattedMessage {...utilsMessages.freeServiceFee} />
                                </PaymentInformationLabel>
                                <DiscountValue>-{money(cart?.deliveryFee?.fee)}đ</DiscountValue>
                              </PaymentInformationRow>
                            )}
                            {discountValue > 0 && (
                              <PaymentInformationRow>
                                <PaymentInformationLabel>
                                  <FormattedMessage {...utilsMessages.voucherLabel} />
                                </PaymentInformationLabel>
                                <DiscountValue>-{money(discountValue)}đ</DiscountValue>
                              </PaymentInformationRow>
                            )}
                            <PaymentInformationRow>
                              <PaymentInformationLabel>
                                VAT
                                <VatTooltip
                                  productVat={cart?.cartSet?.[0]?.totalVatOnProducts}
                                  serviceFeeVat={
                                    cart?.deliveryFee?.isFreeDelivery ? 0 : cart?.deliveryFee?.additionalVat
                                  }
                                />
                              </PaymentInformationLabel>
                              <PaymentInformationLabel>{money(totalVat)}đ</PaymentInformationLabel>
                            </PaymentInformationRow>
                            <Divider />
                            <Space height={4} />
                            <PaymentInformationRow>
                              <TotalLabel>
                                <FormattedMessage {...utilsMessages.total} />
                              </TotalLabel>
                              <TotalValue>{money(finalTotal)}đ</TotalValue>
                            </PaymentInformationRow>
                            <Space height={12} />

                            <FooterAction>
                              <ButtonWrapper>
                                <Button size="large" onClick={onGoPreviousStep}>
                                  <FormattedMessage {...utilsMessages.back} />
                                </Button>
                              </ButtonWrapper>
                              <ButtonWrapper>
                                <Button
                                  type="primary"
                                  disabled={orderDisabled || !cart?.deliveryFee?.fee}
                                  size="large"
                                  loading={cart?.loading || checkoutLoading}
                                  onClick={onCheckoutCompleted}
                                >
                                  <FormattedMessage {...utilsMessages.placeOrder} />
                                </Button>
                              </ButtonWrapper>
                            </FooterAction>
                          </PaymentDetails>
                        </PaymentInformationContainer>
                      </StepItem>
                    </ErrorBoundary>
                  );
                })}
            </Spin>

            {openModalSomthingChanged && (
              <Modal open={openModalSomthingChanged} closable={false} footer={false}>
                <ModalWrapper>
                  <Image src={notAvailableProductImage} alt={'not available products image'} loading="lazy" />
                  <div>
                    <Title>
                      <FormattedMessage {...utilsMessages.somethingChanged} />
                    </Title>
                    <Description>
                      <FormattedMessage {...utilsMessages.somethingChangedDescription} />
                    </Description>
                  </div>
                  <ButtonGoToCart
                    type="primary"
                    onClick={() => {
                      onGoToStep1(true);
                    }}
                  >
                    <FormattedMessage {...utilsMessages.goToCart} />
                  </ButtonGoToCart>
                </ModalWrapper>
              </Modal>
            )}
            <InValidVoucherPopup voucherStatus={voucherStatus} />
          </StepContainer>
        );
      }}
    </Viewport.Consumer>
  );
};
